import { Portal } from "client/Components";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useSynchronizedRef,
	useUnmountedCancellation
} from "client/Hooks";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	getDocumentsViewStructure,
	DocumentsViewContainerViewModel,
	DocumentsViewsStructureModel,
	getBreadCrumbsContentByFolder,
	LoadedSiteContentViewModel
} from "client/Api/Documents/SharePoint/sharepointDocuments";
import {
	DocumentsViewModel,
	FolderViewModel,
	Permissions
} from "client/Api/Documents/SharePoint/common";
import { Folderish, RootFolder } from "client_legacy/Documents/SharePoint/models";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { ItemSelector, SelectedFolder as SharePointFolder} from "./ItemSelector";
import {
	flattenItems,
	isSharePointContainerItem
} from "../../SharePoint/SharePointSideBarMenu";
import {
	applySiteContent,
	createRootFolderFromView
} from "../../SharePoint/SharePointStore";
import {
	createStackItem,
	FolderNavigationStackItem
} from "../../SharePoint/useFolderNavigationStack";
import {
	isViewVisible,
	SideBarMenu,
	SideBarMenuHandle
} from "./SideBarMenu";
import {
	CommonItemPickerOptions,
	SharePointItemPickerEnabledOptions,
	SharePointPickerLocation
} from "../ItemPicker";
import { GridItem } from "../../SharePoint/SharePointOverView";
import { DocumentStoreType } from "client/Api/Documents/common";

export type ViewsLoadingState = { kind: "loading"; }
	| { kind: "error"; }
	| {
		kind: "success";
		items: readonly DocumentsViewContainerViewModel[];
	};

export type LoadingStatus = "hidden" | "visible";

export type InitialSharepointLocation = {
	folder: RootFolder | FolderViewModel | nil;
	viewId: string | undefined;
};

export type SharePointPickerStoreProps = {
	sideBarContainer: HTMLElement;
	commonOptions: CommonItemPickerOptions;
	options: SharePointItemPickerEnabledOptions;
	onSidebarLoadingFailure: (
		reloadSidebar: () => void
	) => void;
	onFilesSelected: (files: readonly GridItem[]) => void;
	selectedItems: readonly GridItem[];
	onLocationChange(location: SharePointPickerLocation): void;
	isActive: boolean;
	location: SharePointPickerLocation | undefined;
	isMobile: boolean;
	onSidebarClose: () => void;
	shouldOpenFileOnClick: boolean;
};

export const SharePointPickerStore = (props: SharePointPickerStoreProps): ReactElement => {
	const {
		sideBarContainer,
		commonOptions,
		options,
		onSidebarLoadingFailure,
		onLocationChange,
		location,
		onFilesSelected,
		selectedItems,
		isActive,
		isMobile,
		onSidebarClose,
		shouldOpenFileOnClick
	} = props;

	const [viewsLoadingState, setViewsLoadingState] = useState<ViewsLoadingState>({ kind: "loading" });
	const stack: readonly FolderNavigationStackItem[] = useFolderStack(
		viewsLoadingState.kind === "loading"
			? undefined
			: location?.folder
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const initialLocationRef: ReadonlyRefObject<SharePointPickerLocation | undefined> = useSynchronizedRef(location);
	const sideBarRef: RefObject<SideBarMenuHandle | null> = useRef<SideBarMenuHandle>(null);
	const isActiveRef: ReadonlyRefObject<boolean> = useSynchronizedRef(isActive);
	const stableOnLocationChange: typeof onLocationChange = useStableImperativeCallback(onLocationChange);

	const currentView = location?.folder.view;

	const loadViews = useCallback(
		async (signal: AbortSignal): Promise<void> => {
			try {
				setViewsLoadingState({ kind: "loading" });

				const result: DocumentsViewsStructureModel = await getDocumentsViewStructure(
					!options.shouldShowPersonalViews,
					signal
				);

				const {
					selectedViewId,
					structure
				} = result;

				if(isActiveRef.current) {
					let folder: SharePointFolder | undefined = initialLocationRef.current
						?.folder;

					if (folder) {
						const viewInStructure: DocumentsViewModel | null = findVisibleView(
							folder.view
								.id,
							structure,
							options
						);

						if (!viewInStructure) {
							folder = undefined;
						}
					} else {
						const viewInStructure: DocumentsViewModel | null = findVisibleView(
							selectedViewId,
							structure,
							options
						);

						if (viewInStructure) {
							folder = {
								...createRootFolderFromView(viewInStructure),
								view: viewInStructure
							};
						}
					}

					if (!folder) {
						const fallbackView: DocumentsViewModel | null = getFallbackView(
							structure,
							options
						);

						if (fallbackView) {
							folder = {
								...createRootFolderFromView(fallbackView),
								view: fallbackView
							};
						}
					}

					if (!folder) {
						throw new Error("No view is selected");
					}

					stableOnLocationChange({
						kind: DocumentStoreType.SharePoint,
						folder
					});
				}

				setViewsLoadingState({
					kind: "success",
					items: structure
				});

			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setViewsLoadingState({ kind: "error" });
			}
		},

		[
			stableOnLocationChange,
			alertManager,
			isActiveRef,
			initialLocationRef,
			options
		]
	);

	const folderPermissionsLoadedHandler = useCallback(
		(
			viewId: string,
			folderId: number | null,
			permissions: Permissions
		): void => {
			if (
				location?.folder.viewId === viewId
				&& location.folder.id === folderId
				&& !location.folder.permissions
			) {
				onLocationChange({
					kind: DocumentStoreType.SharePoint,
					folder: {
						...location.folder,
						permissions
					}
				});
			}
		},
		[location, onLocationChange]
	);

	const selectViewHandler = useCallback(
		(view: DocumentsViewModel): void => {
			if (isMobile) {
				onSidebarClose();
			}

			const currentFolderId: number | nil = location?.folder
				?.id;

			const currentViewId: string | undefined = location?.folder
				?.view
				.id;

			if (isNil(currentFolderId) && currentViewId === view.id) {
				return;
			}

			const folder: RootFolder = createRootFolderFromView(view);

			onLocationChange({
				kind: DocumentStoreType.SharePoint,
				folder: {
					...folder,
					view
				}
			});
		},
		[
			isMobile,
			onLocationChange,
			onSidebarClose,
			location?.folder?.id,
			location?.folder?.view.id
		]
	);

	const openItemHandler = useCallback(
		(folder: Folderish): void => {
			if (isNotNil(currentView)) {
				onLocationChange({
					kind: DocumentStoreType.SharePoint,
					folder: {
						...folder,
						view: currentView,
					}
				});
			}
		},
		[currentView, onLocationChange]
	);

	useEffect(
		(): () => void => {
			const cancellation = new AbortController();
			loadViews(cancellation.signal);

			return () => cancellation.abort();
		},
		[loadViews]
	);

	useEffect(
		(): void => {
			if (viewsLoadingState.kind === "success"
				&& isActive
				&& currentView
			) {
				ensureNotNil(sideBarRef.current)
					.expandParentNodes(currentView.id);
			}
		},
		[
			currentView,
			isActive,
			viewsLoadingState.kind
		]
	);

	const siteContentLoadedHandler = useCallback(
		(id: string, content: LoadedSiteContentViewModel): void => {
			setViewsLoadingState(
				(x): ViewsLoadingState => {
					if (x.kind !== "success") {
						return x;
					}

					return {
						...x,
						...applySiteContent(
							x,
							id,
							content
						)
					};
				}
			);
		},
		[]
	);

	const renderContent = (): ReactElement | null => {
		if (
			!isActive
			|| viewsLoadingState.kind === "error"
		) {
			return null;
		}

		return (
			<ItemSelector
				folderPath={stack}
				currentFolder={
					viewsLoadingState.kind === "loading"
						? undefined
						: location?.folder
				}
				view={currentView}
				onFolderPermissionsLoaded={folderPermissionsLoadedHandler}
				commonOptions={commonOptions}
				onOpenItem={openItemHandler}
				onFilesSelected={onFilesSelected}
				selectedItems={selectedItems}
				isMobile={isMobile}
				shouldOpenFileOnClick={shouldOpenFileOnClick}
			/>
		);
	};

	return (
		<>
			<Portal container={sideBarContainer}>
				<SideBarMenu
					viewsLoadingState={viewsLoadingState}
					onSelectView={selectViewHandler}
					selectedView={isActive ? currentView : undefined}
					options={options}
					onReloadSideBar={() => loadViews(globalCancellation)}
					onSidebarLoadingFailure={onSidebarLoadingFailure}
					onSiteContentLoaded={siteContentLoadedHandler}
					ref={sideBarRef}
				/>
			</Portal>
			{renderContent()}
		</>
	);
};

SharePointPickerStore.displayName = "SharePointPickerStore";

function findVisibleView(
	viewId: string,
	structure: readonly DocumentsViewContainerViewModel[],
	options: SharePointItemPickerEnabledOptions
): DocumentsViewModel | null {
	const view: DocumentsViewModel | null = getViewModelById(viewId, structure);

	if (view !== null && isViewVisible(view, options)) {
		return view;
	} else {
		return null;
	}
}

function getViewModelById(
	viewId: string,
	structure: readonly DocumentsViewContainerViewModel[]
): DocumentsViewModel | null {
	for (const node of flattenItems(structure)) {
		if (!isSharePointContainerItem(node) && node.id === viewId) {
			return node;
		}
	}

	return null;
}

function getFallbackView(
	documentViewContainers: readonly DocumentsViewContainerViewModel[],
	options: SharePointItemPickerEnabledOptions
): DocumentsViewModel | null {
	for (const container of documentViewContainers) {
		const { content } = container;
		if (!content.isLoaded) {
			continue;
		}

		for (const view of content.views) {
			if (isViewVisible(view, options)) {
				return view;
			}
		}
	}

	for (const container of documentViewContainers) {
		const { content } = container;
		if (!content.isLoaded) {
			continue;
		}

		const view: DocumentsViewModel | null = getFallbackView(content.subContainers, options);
		if (view !== null) {
			return view;
		}
	}

	return null;
}

function useFolderStack(selectedFolder: SharePointFolder | undefined): readonly FolderNavigationStackItem[]{
	const [stack, setStack] = useState<readonly FolderNavigationStackItem[]>([]);
	const savedStacksRef = useRef<Map<string, readonly FolderNavigationStackItem[]>>(new Map());

	const loadBreadCrumbs = useCallback(
		async (folder: SharePointFolder | undefined, signal: AbortSignal): Promise<void> => {
			if (isNil(folder)) {
				return;
			}

			const resultStack: FolderNavigationStackItem[] = [
				createStackItem(
					createRootFolderFromView(folder.view)
				)
			];

			if (folder.isRoot) {
				 setStack(resultStack);
				 return;
			}

			const currentStackItem: FolderNavigationStackItem = createStackItem({...folder, isRoot: false});

			const currentFolderUniqueId: string = `${folder.id}${folder.viewId}`;

			const currentSavedStack: readonly FolderNavigationStackItem[] | undefined =
				savedStacksRef.current
					.get(currentFolderUniqueId);

			if (isNotNil(currentSavedStack)) {
				setStack(currentSavedStack);
				return;
			}

			const parentFolderUniqueId: string = `${folder.parentFolderId}${folder.viewId}`;
			const parentSavedStack: readonly FolderNavigationStackItem[] | undefined =
				savedStacksRef.current
					.get(parentFolderUniqueId);

			if (isNotNil(parentSavedStack)) {
				const newStack = [...parentSavedStack, currentStackItem];

				savedStacksRef.current
					.set(currentFolderUniqueId, newStack);

				setStack(newStack);
				return;
			}

			// While loading the breadcrumbs from the backend (which could take some time), we already
			// Show a abbreviated version of the stack so the rest of the folder content can load already
			setStack([...resultStack, currentStackItem]);

			try {
				const stackItemsInBetween: FolderViewModel[] = await getBreadCrumbsContentByFolder(
					folder.id,
					folder.location,
					signal
				);

				stackItemsInBetween.forEach((x: FolderViewModel) => {
					resultStack.push(createStackItem({...x, isRoot: false}));
				});

				resultStack.push(currentStackItem);

				savedStacksRef.current
					.set(currentFolderUniqueId, resultStack);

				setStack(resultStack);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				throw e;
			}
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			const cancellation = new AbortController();
			loadBreadCrumbs(selectedFolder, cancellation.signal);
			return (): void => cancellation.abort();
		},
		[loadBreadCrumbs, selectedFolder]
	);

	return stack;
}